import {HttpClient, HttpParams, HttpHeaders} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {WindowRefService} from "../window-ref.service";
import {
  HTTPRecommendationsResult,
  HTTPSearchPostFilterResponse,
  HTTPSearchRequest,
  HTTPSearchResponse,
  HTTPSearchResult, HTTPSearchResultFiltersResponse, HttpSearchResultTagsRequest,
  HttpSearchResultTagsResponse, HTTPSearchResultTourMap,
  State
} from '../../models';
import {SearchQueryParamsService} from "./query-params.service";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../config/hotel-tours-list-page.config";

@Injectable({providedIn: 'root'})
export class HttpSearchService {
  private host: string;

  constructor(
    private http: HttpClient,
    private windowRef: WindowRefService,
    private queryParams: SearchQueryParamsService,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
  ) {
    this.host = hotelToursPageConfig.httpSearchUrl;
  }

  startSearch(request: HTTPSearchRequest, eventIndex?: number): Observable<HTTPSearchResponse> {
    return this.http.post<HTTPSearchResponse>(
      `${this.host}/web`,
      {...request},
      {
        params: this.defaultQueryParams(eventIndex),
        headers: this.getDebugHeaders(eventIndex)
      }
    );
  }

  startPostFiltersSearch(searchID: string, state: State): Observable<HTTPSearchPostFilterResponse> {
    const payload = {
      ukey: this.windowRef.nativeWindow.app.ukey,
      bkey: this.windowRef.nativeWindow.app.bkey,
      nightsList: state.applyedFilters.nightsList,
      regionsIds: state.applyedFilters.regionsIds,
      stars: state.applyedFilters.stars,
    };

    return this.http
      .post<HTTPSearchPostFilterResponse>(
        `${this.host}/web/${searchID}/postfilter-search`,
        {...payload},
        {params: this.queryParams.defaultQueryParams()}
      );
  }

  getTours(searchID: string, state: State): Observable<HTTPSearchResult> {
    const defaultSortVariant = this.hotelToursPageConfig.searchDefaultSortVariant
      || this.hotelToursPageConfig.searchDefaultSortTestVariant;
    const queryParams = new HttpParams()
      .set('id', searchID)
      .set('page', state.page.toString())
      .set('size', this.hotelToursPageConfig.pageSize)
      .set('sort', state.sort)
      .set('currency', this.windowRef.nativeWindow.app.currency)
      .set('query-id', Date.now().toString() + Math.random().toString(36).substring(2))
      .set('query-start-time', Date.now());

    if (!defaultSortVariant) {
      const headers = new HttpHeaders()
        .set('X-AB-Test', 'sort_by_sales_price');
      return this.http
        .post(
          `${this.host}/web/${searchID}`,
          {...state.applyedFilters},
          {
            params: queryParams,
            headers: headers
          }
        ).pipe(
          switchMap((response: HTTPSearchResult | null) => {
            if (response === null) {
              const emptyResponse: HTTPSearchResult = {searchIsDone: true, pages: 0, total: 0, tours: []};
              return of(emptyResponse);
            }
            return of(response as HTTPSearchResult);
          })
        );
    } else {
      return this.http
        .post(
          `${this.host}/web/${searchID}`,
          {...state.applyedFilters},
          {
            params: queryParams,
          }
        ).pipe(
          switchMap((response: HTTPSearchResult | null) => {
            if (response === null) {
              const emptyResponse: HTTPSearchResult = {searchIsDone: true, pages: 0, total: 0, tours: []};
              return of(emptyResponse);
            }
            return of(response as HTTPSearchResult);
          })
        );
    }
  }

  getFilters(searchID: string, state: State): Observable<HTTPSearchResultFiltersResponse> {
    return this.http.post<HTTPSearchResultFiltersResponse>(
      `${this.host}/web/${searchID}/filters`,
      {...state.applyedFilters, groupMode: 100},
      {params: this.queryParams.defaultQueryParams()}
    );
  }

  getTags(searchID: string, params: HttpSearchResultTagsRequest): Observable<HttpSearchResultTagsResponse> {
    return this.http.post<HttpSearchResultTagsResponse>(
        `${this.host}/${searchID}/smartfilters/tags`,
        {params},
        {params: this.defaultQueryParams()}
    );
  }

  getMapTours(searchID: string, state: State): Observable<HTTPSearchResultTourMap[]> {
    return this.http.post<HTTPSearchResultTourMap[]>(
      `${this.host}/web/${searchID}/map`,
      {...state.applyedFilters, groupMode: 4},
      {params: this.queryParams.defaultQueryParams()}
    );
  }

  getHotelRecommendations(searchID: string): Observable<HTTPRecommendationsResult> {
    return this.http.get<HTTPRecommendationsResult>(
      `${this.host}/web/${searchID}/recommendations`,
      {params: this.queryParams.defaultQueryParams()}
    );
  }


  getHotelRecommendationsWithFilters(searchID: string, state: State): Observable<HTTPRecommendationsResult> {
    return this.http.post<HTTPRecommendationsResult>(
      `${this.host}/web/${searchID}/recommendations`,
      {...state},
      {params: this.queryParams.defaultQueryParams()}
    );
  }

  private defaultQueryParams(eventIndex?: number) {
    const page = this.windowRef.pageAppParams;
    return new HttpParams()
        .set('query-id', Date.now().toString() + Math.random().toString(36).substring(2))
        .set('query-start-time', Date.now())
        .set('dbg-type', page.isMobile ? 'mobile' : 'desktop')
        .set('dbg-event-id', eventIndex || -1)
        ;
  }

  getDebugHeaders(eventIndex?: number): HttpHeaders {
    const page = this.windowRef.pageAppParams;

    const params = [];
    // порядок такой, чтобы в логе плюс-минус друг под другом были параметры
    params.push('pageType=' + (page.isMobile ? 'mobile' : 'desktop'));
    params.push('pageLoad=' + (page.openTimestamp || -1));
    params.push('eventId=' + (eventIndex || -1));
    params.push('vUid=' + (page.vUid || -1));
    params.push('ymId=' + (page.ymId || -1));
    params.push('reqAt=' + (new Date()).toISOString());

    const headers = new HttpHeaders();
    headers.set('X-Web-Debug-Info', params.join('; '));

    if (page.debug) {
      console.log('NgDebug headers', {'X-Web-Debug-Info': params.join('; ')});
    }

    return headers;
  }
}
